import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import CosmicBackground from '../components/CosmicBackground';
import CosmicAlignment from '../components/CosmicAlignment';
import { CheckCircle, Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [status, setStatus] = useState('checking');
  const [showAlignment, setShowAlignment] = useState(false);

  const sessionId = searchParams.get('session_id');
  const pollTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const tiktokEventSentRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    if (!sessionId) {
      navigate('/dashboard', { replace: true });
      return;
    }

    checkPaymentStatus();

    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const getPurchasePayload = () => {
    const isAnnual = searchParams.get('billing_period') === 'annual';

    return isAnnual
      ? {
          currency: 'USD',
          value: 59.0,
          content_name: 'AskUniverse Premium Annual',
          content_type: 'product',
          content_id: 'premium_annual',
        }
      : {
          currency: 'USD',
          value: 9.99,
          content_name: 'AskUniverse Premium Monthly',
          content_type: 'product',
          content_id: 'premium_monthly',
        };
  };

  const updateLocalUser = async () => {
    try {
      const me = await api.get('/auth/me');
      localStorage.setItem('user', JSON.stringify(me.data));
      return me.data;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  };

  const getUserIdentifier = (user) => {
    return user?.id || user?._id || user?.user_id || user?.email || undefined;
  };

  const identifyTikTokUser = (user) => {
    try {
      const email = user?.email;
      const externalId = getUserIdentifier(user);

      if (window.ttq && (email || externalId)) {
        window.ttq.identify({
          ...(email ? { email } : {}),
          ...(externalId ? { external_id: String(externalId) } : {}),
        });
      }
    } catch (error) {
      console.error('TikTok identify error:', error);
    }
  };

  const sendTikTokCompletePayment = async () => {
    if (tiktokEventSentRef.current) return;

    try {
      const user = await updateLocalUser();
      identifyTikTokUser(user);

      const payload = getPurchasePayload();

      if (window.trackTikTokEvent) {
        window.trackTikTokEvent('CompletePayment', payload);
        window.trackTikTokEvent('Purchase', payload);
        window.trackTikTokEvent('Subscribe', payload);
      }

      tiktokEventSentRef.current = true;
    } catch (error) {
      console.error('TikTok payment event error:', error);
    }
  };

  const checkPaymentStatus = async () => {
    const maxAttempts = 6;
    let attempts = 0;

    const poll = async () => {
      if (!isMountedRef.current) return;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await api.get(`/subscription/checkout-status/${sessionId}`);
        const paymentStatus = response?.data?.payment_status;
        const checkoutStatus = response?.data?.status;

        if (paymentStatus === 'paid' || checkoutStatus === 'complete') {
          await sendTikTokCompletePayment();

          if (!isMountedRef.current) return;

          setStatus('success');
          setShowAlignment(true);
          return;
        }

        if (checkoutStatus === 'expired') {
          setStatus('failed');
          return;
        }

        attempts += 1;
        pollTimeoutRef.current = setTimeout(poll, 2000);
      } catch (error) {
        console.error('Payment confirmation error:', error);
        attempts += 1;

        if (attempts >= maxAttempts) {
          setStatus('error');
          return;
        }

        pollTimeoutRef.current = setTimeout(poll, 2000);
      }
    };

    poll();
  };

  const handleAlignmentComplete = () => {
    setShowAlignment(false);
    setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 500);
  };

  const handleReturnToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      <CosmicBackground />
      <div
        className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-violet-900/20 via-slate-900 to-slate-950 pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <AnimatePresence>
        {showAlignment && <CosmicAlignment onComplete={handleAlignmentComplete} />}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 text-center"
      >
        <div className="glass-card p-12 max-w-md mx-auto">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-violet-500 mx-auto mb-4 animate-spin" />
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {t('processingPayment')}
              </h1>
              <p className="text-slate-400">{t('pleaseWaitConfirm')}</p>
            </>
          )}

          {status === 'success' && !showAlignment && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {t('paymentSuccessful')}
              </h1>
              <p className="text-slate-400">{t('preparingAlignment')}</p>
            </>
          )}

          {status === 'failed' && (
            <>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {t('paymentFailed')}
              </h1>
              <p className="text-slate-400 mb-4">{t('paymentSessionExpired')}</p>
              <button
                onClick={handleReturnToDashboard}
                className="px-6 py-3 bg-violet-600 text-white rounded-full hover:bg-violet-500 transition-all"
              >
                {t('returnToDashboard')}
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {t('checkYourEmail')}
              </h1>
              <p className="text-slate-400 mb-4">{t('paymentVerificationTimeout')}</p>
              <button
                onClick={handleReturnToDashboard}
                className="px-6 py-3 bg-violet-600 text-white rounded-full hover:bg-violet-500 transition-all"
              >
                {t('returnToDashboard')}
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {t('error')}
              </h1>
              <p className="text-slate-400 mb-4">{t('somethingWentWrong')}</p>
              <button
                onClick={handleReturnToDashboard}
                className="px-6 py-3 bg-violet-600 text-white rounded-full hover:bg-violet-500 transition-all"
              >
                {t('returnToDashboard')}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Success;
